// NEVANU — DOM Utilities
// Sichere HTML-Erzeugung, Toast-Nachrichten, etc.

/** HTML-Entity-Encoding gegen XSS */
export function escHtml(str: any): string {
  if (str === null || str === undefined) return '';
  const s = String(str);
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return s.replace(/[&<>"']/g, c => map[c] || c);
}

/** Attribut-Encoding */
export function escAttr(str: any): string {
  if (str === null || str === undefined) return '';
  return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

/** Toast-Nachricht anzeigen */
export function showToast(message: string, type: 'success' | 'error' | 'info' = 'success', duration: number = 3000): void {
  // Bestehende Toasts entfernen
  document.querySelectorAll('.toast-msg').forEach(el => el.remove());

  const toast = document.createElement('div');
  toast.className = 'toast-msg';
  toast.textContent = message;

  const colors: Record<string, string> = {
    success: '#2ecc71',
    error: '#e74c3c',
    info: '#3498db',
  };

  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: colors[type] || colors.success,
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '50px',
    fontSize: '.85rem',
    fontWeight: '500',
    zIndex: '99999',
    opacity: '0',
    transition: 'opacity .3s',
    textAlign: 'center',
    maxWidth: '90vw',
    fontFamily: 'Outfit, sans-serif',
  });

  document.body.appendChild(toast);
  requestAnimationFrame(() => { toast.style.opacity = '1'; });

  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

/** Bottom Sheet Swipe-Geste hinzufügen (overlayEl = der .bottom-sheet-overlay Container) */
export function addBottomSheetSwipe(overlayEl: HTMLElement, onClose: () => void): void {
  const sheet = overlayEl.querySelector('.bottom-sheet') as HTMLElement;
  if (!sheet) return;
  sheet.style.overscrollBehavior = 'contain';
  let startY = 0;
  let currentY = 0;
  let isDragging = false;
  let couldDrag = false;

  // Prevent background scroll/pull-to-refresh while sheet is open
  overlayEl.addEventListener('touchmove', (e: TouchEvent) => {
    if (isDragging) { e.preventDefault(); return; }
    const isInside = sheet.contains(e.target as Node);
    if (!isInside) e.preventDefault();
  }, { passive: false });

  sheet.addEventListener('touchstart', (e: TouchEvent) => {
    startY = e.touches[0].clientY;
    currentY = startY;
    isDragging = false;
    couldDrag = (sheet.scrollTop <= 0);
  }, { passive: true });

  sheet.addEventListener('touchmove', (e: TouchEvent) => {
    currentY = e.touches[0].clientY;
    const dy = currentY - startY;
    if (!isDragging && couldDrag && dy > 10) {
      isDragging = true;
      startY = currentY;
      sheet.style.transition = 'none';
    }
    if (!isDragging) return;
    e.preventDefault();
    const diff = currentY - startY;
    if (diff > 0) {
      sheet.style.transform = `translateY(${diff}px)`;
      const opacity = Math.max(0, 0.5 - (diff / 400));
      overlayEl.style.background = `rgba(0,0,0,${opacity})`;
    }
  }, { passive: false });

  sheet.addEventListener('touchend', () => {
    if (!isDragging) { couldDrag = false; return; }
    isDragging = false;
    couldDrag = false;
    const diff = currentY - startY;
    sheet.style.transition = '';
    if (diff > 60) {
      onClose();
    } else {
      sheet.style.transform = '';
      overlayEl.style.background = 'rgba(0,0,0,.5)';
    }
  });
}

/** Datum formatieren (Schweizer Format) */
export function formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', options || {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Datum für Input-Felder (YYYY-MM-DD) */
export function dateToInput(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
